import React from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { PromoteMediaDraft } from '../draftStore';
import { composerMediaMeta, composerMediaTitle } from './composerMediaText';
import { pickerMessages } from './picker/pickerMessages';
import { PickerFileList } from './picker/PickerFileList';
import { PickerSongBar } from './picker/PickerSongBar';
import { PickerTile } from './picker/PickerTile';
import { PickerUploadBox } from './picker/PickerUploadBox';
import { PickerUploadsSection } from './picker/PickerUploadsSection';
import { HANDOVER_KEY, useComposerMediaPicker } from './picker/useComposerMediaPicker';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './picker/ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerAttachMediaModalProps {
  isOpen: boolean;
  accountId: number;
  /** The catalog songs, for the song selector. */
  items: CatalogItem[];
  /** The release that the Composer already picked: the picker opens on it. */
  releaseUuid?: string;
  /** The file a Promote screen handed over ("Post it"), if one waits. */
  available?: PromoteMediaDraft;
  closeModal: () => void;
  /** Attaches the picked, handed-over or uploaded file. */
  onAttach: (media: PromoteMediaDraft) => void;
  /** Opens the Studio, on the song when one is picked. */
  onOpenStudio: (catalogItemUuid: string | undefined) => void;
}

/**
 * The song that the picker opens on: the release of the post, else the song of the attached file
 * (QA finding B16). Undefined: the picker takes the newest song with a ready file. Pure.
 */
export function pickerReleaseOf(
  releaseUuid: string | undefined,
  media: Pick<PromoteMediaDraft, 'catalogItemUuid'> | undefined
): string | undefined {
  return releaseUuid ?? media?.catalogItemUuid;
}

type PickerBodyProps = Omit<ComposerAttachMediaModalProps, 'isOpen'>;

/**
 * The body and the footer of the picker. The dialog mounts it on each open and unmounts it on
 * close (reactstrap `unmountOnClose`), so its state (song, filter, selection, upload) starts fresh
 * on every open, and an upload that still runs at Cancel or close stops (`usePickerUpload`).
 */
const ComposerAttachMediaBodyComponent: React.FC<PickerBodyProps> = ({
  accountId,
  items,
  releaseUuid,
  available,
  closeModal,
  onAttach,
  onOpenStudio,
}) => {
  const intl = useIntl();
  const picker = useComposerMediaPicker({ accountId, items, releaseUuid, handover: available, onAttach });

  return (
    <>
      <ModalBody>
        <div className={styles.pickerBody} data-testid="composer-media-picker">
          {available !== undefined && (
            <section>
              <span className={styles.sectionName}>{intl.formatMessage(pickerMessages.handover)}</span>
              <ul className={styles.tileList}>
                <PickerTile
                  tileKey={HANDOVER_KEY}
                  name={composerMediaTitle(intl, available)}
                  facts={composerMediaMeta(intl, available)}
                  kind={available.kind ?? 'video'}
                  pictureUrl={available.thumbnailUrl ?? null}
                  isSelected={picker.selectedKey === HANDOVER_KEY}
                  onSelect={picker.select}
                  onAttach={picker.attachKey}
                />
              </ul>
            </section>
          )}
          <PickerSongBar
            items={items}
            song={picker.song}
            onSong={picker.changeSong}
            filter={picker.filter}
            onFilter={picker.setFilter}
          />
          <PickerFileList picker={picker} onOpenStudio={onOpenStudio} />
          {/* design: assets-rethink a1, decision 8. The uploads live here, not on a Library page. */}
          <PickerUploadsSection picker={picker} />
          <PickerUploadBox
            state={picker.upload.state}
            onUpload={picker.upload.upload}
            onCancel={picker.upload.cancel}
            onRetry={picker.upload.retry}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <SMLButton onClick={closeModal}>{intl.formatMessage(pickerMessages.cancel)}</SMLButton>
        <SMLButton variant="primary" disabled={!picker.canAttach} onClick={picker.attachSelected}>
          {intl.formatMessage(pickerMessages.attach)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

const ComposerAttachMediaBody = TypedMemo(ComposerAttachMediaBodyComponent);

/**
 * "Attach media": the images and videos made for a song (the newest ready revision of each asset
 * and each AI reel, as on the Assets tab), the file a Promote screen handed over, and an image or a
 * video uploaded from the device. A click selects a file, "Attach" (or a double click) attaches it.
 */
const ComposerAttachMediaModalComponent: React.FC<ComposerAttachMediaModalProps> = ({ isOpen, ...props }) => {
  const intl = useIntl();
  const { closeModal } = props;
  // The body takes every prop but `isOpen`; it mounts on each open, so no state of a past open stays.

  return (
    <Modal isOpen={isOpen} toggle={closeModal} centered size="lg" scrollable unmountOnClose className="sml-modal">
      <ModalHeader toggle={closeModal}>{intl.formatMessage(pickerMessages.title)}</ModalHeader>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- the same props, passed on to the body */}
      {isOpen ? <ComposerAttachMediaBody {...props} /> : null}
    </Modal>
  );
};

export const ComposerAttachMediaModal = TypedMemo(ComposerAttachMediaModalComponent);
